import { ref } from 'vue';

/**
 * Native HTML5 drag-and-drop sort composable.
 * No external dependencies — uses draggable / dragstart / dragover / drop / dragend.
 * Arrow buttons (move up/down) provide the keyboard-accessible alternative path.
 */
export function useDragSort<T extends { id: string }>(
  items: () => T[],
  onReorder: (reordered: T[]) => void,
) {
  const draggingId = ref<string | null>(null);
  const overIndex = ref<number | null>(null);

  function onDragStart(id: string) {
    draggingId.value = id;
  }

  function onDragOver(index: number) {
    overIndex.value = index;
  }

  function onDrop() {
    if (draggingId.value === null || overIndex.value === null) return;

    const arr = [...items()];
    const fromIdx = arr.findIndex((i) => i.id === draggingId.value);
    if (fromIdx === -1) return;

    const [item] = arr.splice(fromIdx, 1);
    arr.splice(overIndex.value, 0, item);

    onReorder(arr);
    draggingId.value = null;
    overIndex.value = null;
  }

  function onDragEnd() {
    draggingId.value = null;
    overIndex.value = null;
  }

  return { draggingId, overIndex, onDragStart, onDragOver, onDrop, onDragEnd };
}
